import { EntityDataUsuario } from "../Domain/EntityDataUsuario";
import { AdapterService } from "./AdapterService";
import { AdapterStorage } from "./AdapterStorage";

export type TypeModeNotification = 'ALL' | 'INDIVIDUAL' | 'GROUP';
export class AdapterNotification {
    private readonly VAPID_PUBLIC_KEY = process.env.REACT_APP_KEY_NOTIFICATION ?? '';

    private async getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
        if ('serviceWorker' in navigator) return await navigator.serviceWorker.ready;
        return null;
    }

    public async hasNotificationPermission(): Promise<boolean> {
        return Notification.permission === 'granted';
    }

    public async hasActiveSubscription(): Promise<boolean> {
        const registration = await this.getServiceWorkerRegistration();
        if (!registration) return false;

        const subscription = await registration.pushManager.getSubscription();
        return subscription !== null;
    }

    public async subscribe(user: EntityDataUsuario, modeNotification: TypeModeNotification = 'INDIVIDUAL'): Promise<boolean | null> {
        const registration = await this.getServiceWorkerRegistration();
        if (!registration) {
            return null;
        }

        if (Notification.permission !== 'granted') {
            const permission = await Notification.requestPermission();
            if (permission !== 'granted') {
                return null;
            }
        }

        const hasActiveSubscription = await this.hasActiveSubscription();
        if (hasActiveSubscription) {
            await this.unsubscribe(user);
        }

        const subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: this.urlBase64ToUint8Array(this.VAPID_PUBLIC_KEY),
        });

        let url : string = `${process.env.REACT_APP_URL_MASTER}/gia/v2/Master/User/subscribeNotification`;
        const body = JSON.stringify({ idUser: user.IdUser, ...subscription.toJSON(), modeNotification });
        const result = await (new AdapterService()).call("POST", url, body, "bearer", "json", 'json', {}, 0);
        if (!result) throw Error('Ocurrió un error al suscribirse');
        this.changeStateNotification(modeNotification)

        return !!result;
    }

    public async unsubscribe(user: EntityDataUsuario): Promise<boolean> {
        const registration = await this.getServiceWorkerRegistration();
        if (!registration) return false;

        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
            const body = JSON.stringify({ idUser: user.IdUser, ...subscription.toJSON()});
            let url : string = `${process.env.REACT_APP_URL_MASTER}/gia/v2/Master/User/desubscribeNotification`;
            const result = await (new AdapterService()).call("POST", url, body, "bearer", "json", 'json', {}, 0)

            if (!result) throw Error('Ocurrió un error al suscribirse');
            await subscription.unsubscribe();
            AdapterStorage.remove('modeNotification');
            return true;
        }

        return false;
    }

    public async changeModeNotification(user: EntityDataUsuario, modeNotification: TypeModeNotification): Promise<boolean> {
        const registration = await this.getServiceWorkerRegistration();
        if (!registration) return false;

        const subscription = await registration.pushManager.getSubscription();
        if (!subscription) return false;

        const body = JSON.stringify({
            idUser: user.IdUser,
            keys: subscription.toJSON().keys,
            modeNotification
        });
        let url : string = `${process.env.REACT_APP_URL_MASTER}/gia/v2/Master/User/changeModeNotification`;
        const result = await (new AdapterService()).call("POST", url, body, "bearer", "json", 'json', {}, 0)

        if (!result) throw Error('Ocurrió un error al cambiar modo de notificación');
        this.changeStateNotification(modeNotification);

        return true;
    }

    public getStateNotification() {
        const { modeNotification } = AdapterStorage.get('modeNotification');
        return modeNotification as TypeModeNotification | null;
    }

    private changeStateNotification(modeNotification: TypeModeNotification) {
        AdapterStorage.set('modeNotification', modeNotification);
    }

    private urlBase64ToUint8Array(base64String: string) {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);

        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    }
}
